<template>
  <router-link
    class="notification-item"
    :class="{ 'notification-item--read': notif.isRead == 1 }"
    :to="link"
    :event="link === '' ? '' : 'click'"
    @click.native="clickNotif"
  >
    <div class="v-centerer">
      <Icon class="notification-item__icon">{{ notificationIcon }}</Icon>
      <div class="notification-item__content">
        <div class="notification-item__text">
          {{ title }}
        </div>
        <div v-quick-title="timestamp" class="notification-item__time">
          {{ timestampSmall }}
        </div>
      </div>
    </div>
  </router-link>
</template>

<script>
/**
 * Notification item - Link
 *
 * @vue-prop {Object} notif - notification data given by the server
 *
 * @vue-computed {String} link - Relative link of the event
 * @vue-computed {String} title - Title of the event, related to its type
 * @vue-computed {String} timestamp - Text, date amd time of the event
 * @vue-computed {String} timestampSmall - Text, time since the event happened
 */

// Components
import Icon from '@/components/icon.vue';
// Utils & assets
import { PROMPT_JOIN_MEETING } from '@/store/modules/meeting/action-types';
import { decodeHTML } from '@/utils/main';
import { NotificationType } from '@/utils/notification-utils';
import {
  hardhatExtraBold,
  infosMedium,
  problemsMedium,
  teamMedium,
  tickMedium,
} from '@/assets/icons/';

export default {

  name: 'NotificationItem',

  components: {
    Icon,
  },

  props: {
    notif: {
      type: Object,
      required: true,
    },
  },

  computed: {
    /** String: Return the right icon according to the notification type */
    notificationIcon() {
      switch (Number(this.notif.type)) {
        case NotificationType.UPCOMING_MEETING.id:
        case NotificationType.MEETING_STARTED.id:
          return teamMedium;
        case NotificationType.ACTION_ASSIGNED.id:
        case NotificationType.ACTION_COMPLETE.id:
        case NotificationType.ACTION_RETURNED.id:
        case NotificationType.USER_ACTION_ASSIGNED.id:
        case NotificationType.USER_ACTION_DEADLINE_OVERDUE.id:
        case NotificationType.USER_ACTION_DEADLINE_APPROACHING.id:
          return tickMedium;
        case NotificationType.PROBLEM_RECEIVED.id:
        case NotificationType.PROBLEM_ACCESS_REVOKED.id:
        case NotificationType.PROBLEM_COMMENT.id:
        case NotificationType.PROBLEM_UPDATE.id:
          return problemsMedium;
        case NotificationType.GW_STARTED.id:
          return hardhatExtraBold;
        case NotificationType.INFO_RELEASED.id:
        default:
          return infosMedium;
      }
    },
    /**
     * If available a link to the related object
     * WARNING: This should keep the same logic as server/notifications/sendEmails.php
     */
    link() {
      switch (Number(this.notif.type)) {
        case NotificationType.UPCOMING_MEETING.id:
        case NotificationType.MEETING_STARTED.id:
          return '';
        case NotificationType.ACTION_ASSIGNED.id:
        case NotificationType.ACTION_COMPLETE.id:
        case NotificationType.ACTION_RETURNED.id:
        case NotificationType.USER_ACTION_ASSIGNED.id:
        case NotificationType.USER_ACTION_DEADLINE_OVERDUE.id:
        case NotificationType.USER_ACTION_DEADLINE_APPROACHING.id:
          return this.getActionCreationLink(this.notif.dataLinkId);
        case NotificationType.PROBLEM_RECEIVED.id:
        case NotificationType.PROBLEM_ACCESS_REVOKED.id:
        case NotificationType.PROBLEM_COMMENT.id:
        case NotificationType.PROBLEM_UPDATE.id:
          return this.getPbSheetLink(this.notif.dataLinkId);
        case NotificationType.GW_STARTED.id:
          return this.getGwOngoingLink(this.notif.dataLinkId);
        case NotificationType.INFO_RELEASED.id:
          return this.getInfoCreationLink(this.notif.dataLinkId);
        default:
          return '';
      }
    },
    /** Title of the notification depending on its type */
    title() {
      return NotificationType[this.notif.codeName].getTitle(decodeHTML(this.notif.dataName));
    },
    timestampSmall() {
      return this.$moment(this.notif.created).fromNow(true);
    },
    timestamp() {
      return this.$moment(this.notif.created).format('lll');
    },
  },

  methods: {
    /** Detect click on notification if it should trigger an action */
    clickNotif() {
      if (this.notif.type === NotificationType.MEETING_STARTED.id) {
        this.$store.dispatch(`meeting/${PROMPT_JOIN_MEETING}`, { id: this.notif.dataLinkId });
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.notification-item {
  padding: 1em;
  border-radius: $high-border-radius;
  display: block;
  cursor: pointer;

  &--read .notification-item__text {
    font-weight: normal;
  }

  &:hover {
    background: $color-shizen-card-hover;
  }

  &__icon {
    padding: 0.4rem;
    width: 1rem;
    height: 1rem;
    border-radius: $main-border-radius;
    background: $color-shizen-light-blue;
    flex: 0 0 auto;
    margin-right: 0.7rem;
  }

  &__content {
    min-width: 0px;
    flex: 1;
  }

  &__text {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__time {
    padding: 0.3rem 0;
    font-size: 0.8rem;
  }
}
</style>
